import Tracking from '@vromo/analytics';
import { useStore } from '@vromo/mobx-react';
import { Job } from '@vromo/models';
import PageLoader from '@vromo/ui/components/PageLoader';
import { Tab, Tabs } from '@vromo/ui/components/Tabs';
import { useToasts } from '@vromo/ui/components/Toasts';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import flattenDeep from 'lodash/flattenDeep';
import { action, runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBatchFetchJobs, { FetchJobParams, MAX_JOBS } from '../dashboard/batchFetchJobs';
import { RootStore } from '../stores/RootStore';
import DriverReport from './DriverReport';
import JobHistory from './JobHistory';
import createCSVForJobs from './JobHistory/helpers/createCSVForJobs';
import JobReport from './JobReport';
import PerformanceReport from './PerformanceReport';
import FilterButton from './components/FilterButton';
import ReportFilterModal from './components/ReportFilterModal';
import {
    containsCampaigns,
    containsDrivers,
    containsPolicies,
    jobNameContains,
    jobStatusMatches
} from './filters/JobFilters';
import { downloadCSV } from './helpers';
import styles from './reportRoot.style';
import { Filters } from './utils/reportTypes';

const DEFAULT_PARAMS: FetchJobParams = {
    _all_finished__order: 'desc',
    _all_attr__include: false,
    _all_route__include: true,
    _all_dispatches__include: true,
    _all_stages__all_analytics_clicksTotal__include: false
};

const FILTER_DATE_FORMAT = 'Do MMMM YYYY';

export const getDefaultFilters = (): Filters => ({
    startDate: startOfDay(subDays(Date.now(), 7)),
    endDate: endOfDay(Date.now()),
    jobName: '',
    jobStatus: null,
    teams: [],
    vehicles: [],
    sites: [],
    policies: [],
    campaigns: []
});

const ReportRoot = observer(() => {
    const { t } = useTranslation();
    const {
        vehicleStore,
        groupStore,
        policyStore,
        campaignStore,
        siteStore,
        authStore: { graph, role }
    }: RootStore = useStore('rootStore');

    const { batchFetchJobs, loadingJobs } = useBatchFetchJobs();

    const showToast = useToasts();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filteredJobList, setFilteredJobList] = useState<Job[]>([]);
    const [filteredCompleteJobs, setFilteredCompleteJobs] = useState<Job[]>([]);
    const [filteredCancelledJobs, setFilteredCancelledJobs] = useState<Job[]>([]);

    const reportStore = useLocalObservable(() => ({
        currentTab: 'report',
        groups: new Map(),
        filters: getDefaultFilters(),

        get filterOptions() {
            return {
                drivers: vehicleStore.vehicles
                    .filter((v) => !v.synthetic || v.parentVehicleId === null)
                    .map((v) => ({
                        label: v.displayName(
                            vehicleStore.driverVehicleMap,
                            groupStore.withId(v.group!)
                        ),
                        value: v.id
                    })),
                groups: groupStore.groups.map((g) => ({ label: g.name || '', value: g.id || 0 })),
                policies: policyStore.policies.map((p) => ({
                    label: p.name || '',
                    value: p.id || 0
                })),
                sites: siteStore.sites
                    .filter((s) => s.contact?.name)
                    .map((s) => ({
                        label: s.contact!.name || '',
                        value: s.id || 0
                    })),
                campaigns: campaignStore.campaigns.map((c) => ({
                    label: c.name || '',
                    value: c.id || 0
                }))
            };
        },

        get filterCount() {
            const { startDate, endDate, jobName, vehicles, sites, teams, policies, campaigns } =
                this.filters;
            const now = Date.now();
            const defaultDiff = [
                !(
                    startDate.valueOf() === startOfDay(subDays(now, 7)).valueOf() &&
                    endDate.valueOf() === endOfDay(now).valueOf()
                ),
                jobName !== '',
                vehicles?.length !== 0,
                sites?.length !== 0,
                teams?.length !== 0,
                policies?.length !== 0,
                campaigns?.length !== 0
            ];

            return defaultDiff.reduce((total, it) => (it ? total + 1 : total), 0);
        },

        async applyFilters(filters: Filters) {
            this.filters = filters;
            const params = {
                ...DEFAULT_PARAMS,
                ...(filters.teams.length && {
                    _all_group__in: flattenDeep(
                        filters.teams.map((t) => [
                            t.value,
                            groupStore.childDictionary.get(+t.value)
                        ])
                    ).join(',')
                }),
                ...(reportStore.filters.sites.length && {
                    _all_site__exists_id__in: filters.sites.map((s) => s.value).join(',')
                })
            };
            setFilteredJobList([]);
            batchFetchJobs({
                params,
                startDate: this.filters.startDate,
                endDate: this.filters.endDate,
                filterFn: filterJobs
            })
                .then((jobs) => {
                    setFilteredJobList(jobs);

                    if (jobs.length >= MAX_JOBS) {
                        showToast(
                            `You've reached the limit of ${MAX_JOBS} jobs - try adjusting the filters on your report.`,
                            'warning'
                        );
                    }
                })
                .catch((e) => {
                    console.error(e);
                    showToast('Something went wrong. Try selecting a smaller date range.', 'error');
                });
        },

        changeTab(tab: string) {
            reportStore.currentTab = tab;
        }
    }));

    const { startDate, endDate } = reportStore.filters;

    useEffect(() => {
        Tracking.track('Spatula Report Opened');

        if (!role.id) return;

        runInAction(() => {
            batchFetchJobs({
                params: DEFAULT_PARAMS,
                startDate,
                endDate,
                filterFn: filterJobs
            }).then((jobs) => setFilteredJobList(jobs));
        });
    }, []);

    useEffect(() => {
        setFilteredCompleteJobs(getFilteredCompleteJobs(filteredJobList));
        setFilteredCancelledJobs(getFilteredCancelledJobs(filteredJobList));
    }, [filteredJobList]);

    const filterJobs = (jobs: Job[]) => {
        const { filters } = reportStore;

        const mapValues = (filter) => filter.map((f) => f.value);

        return jobs.filter((j) => {
            return (
                jobNameContains(j, filters.jobName) &&
                jobStatusMatches(j, filters.jobStatus?.value.toString()) &&
                containsDrivers(j, mapValues(filters.vehicles)) &&
                containsPolicies(j, mapValues(filters.policies, j)) &&
                containsCampaigns(j, mapValues(filters.campaigns))
            );
        });
    };

    const getFilteredCompleteJobs = (jobs: Job[]) =>
        jobs.filter((job) => job.status === 'completed');

    const getFilteredCancelledJobs = (jobs: Job[]) =>
        jobs.filter((job) => job.status === 'cancelled');

    const createJobCsv = action(async () => {
        Tracking.track('Job CSV Downloaded');

        const params = {
            ...DEFAULT_PARAMS,
            _all_attr__include: true,
            _all_stages__all_analytics_clicksTotal__include: true,
            ...(reportStore.filters.teams.length && {
                _all_group__in: flattenDeep(
                    reportStore.filters.teams.map((t) => [
                        t.value,
                        groupStore.childDictionary.get(parseInt(t.value.toString()))
                    ])
                ).join(',')
            }),
            ...(reportStore.filters.sites.length && {
                _all_site__exists_id__in: reportStore.filters.sites.map((s) => s.value).join(',')
            })
        };

        batchFetchJobs({ params, startDate, endDate, filterFn: filterJobs })
            .then((jobs) => {
                downloadCSV(
                    createCSVForJobs(jobs),
                    `vromo-job-export_${format(
                        reportStore.filters.startDate,
                        'YYYY-MM-DD'
                    )}--${format(reportStore.filters.endDate, 'YYYY-MM-DD')}.csv`
                );
            })
            .catch((e) => {
                console.error(e);

                showToast('There was a problem generating the Job CSV.');
            });
    });

    const filterDateText = useMemo(
        () => `${format(startDate, FILTER_DATE_FORMAT)} - ${format(endDate, FILTER_DATE_FORMAT)}`,
        [startDate, endDate]
    );

    const driverReportCSVTitle = useMemo(
        () =>
            `vromo-driver-export_${format(startDate, 'YYYY-MM-DD')}--${format(
                endDate,
                'YYYY-MM-DD'
            )}.csv`,
        [startDate, endDate]
    );

    const openFilters = useCallback(() => setFiltersOpen(true), []);

    return (
        <div className={styles.container}>
            <div className={styles.contentContainer}>
                <PageLoader loading={loadingJobs} />
                <div className={styles.topContent}>
                    <FilterButton
                        {...{ openFilters, filterDateText }}
                        filterCount={reportStore.filterCount}
                    />
                </div>
                <ReportFilterModal
                    isOpen={filtersOpen}
                    applyFilters={reportStore.applyFilters}
                    filters={reportStore.filters}
                    filterOptions={reportStore.filterOptions}
                    onClose={() => setFiltersOpen(false)}
                />
                <Tabs currentTab={reportStore.currentTab} onChange={reportStore.changeTab}>
                    <Tab id="report">{t('reports.jobReport')}</Tab>
                    <Tab id="history">{t('reports.jobHistory')}</Tab>
                    <Tab id="performanceReport">{t('reports.performanceReports')}</Tab>
                    <Tab id="driverReport">{t('reports.driverReport')}</Tab>
                </Tabs>
            </div>
            {reportStore.currentTab === 'report' && (
                <JobReport
                    startDate={reportStore.filters.startDate}
                    endDate={reportStore.filters.endDate}
                    jobsCompleted={filteredCompleteJobs}
                    jobsCancelled={filteredCancelledJobs}
                />
            )}
            {reportStore.currentTab === 'history' && (
                <JobHistory
                    {...{ createJobCsv }}
                    filters={reportStore.filters}
                    jobs={filteredJobList}
                    loadingJobs={loadingJobs}
                />
            )}
            {reportStore.currentTab === 'performanceReport' && (
                <PerformanceReport
                    startDate={reportStore.filters.startDate}
                    endDate={reportStore.filters.endDate}
                    jobs={filteredCompleteJobs}
                />
            )}
            {reportStore.currentTab === 'driverReport' && (
                <DriverReport filters={reportStore.filters} csvTitle={driverReportCSVTitle} />
            )}
        </div>
    );
});

export default ReportRoot;
